// Профиль, кошелёк, обучение, bootstrap — tech/03 §2 (Bootstrap), §3.
import { z } from 'zod';
import { CurrencyCode, Empty, Grant, IsoTime, NextCursor, PageQuery, TutorialStep, Uuid7, strict } from '../common.js';
import { BattleState } from '../combat.js';
import { endpoint } from '../endpoint.js';
import { ContentPointer, CoopBrief, DocId, DocRef, GarageZoneBrief, Item, Player, PlayerSettings, Wallet } from './models.js';
import { RefundNotice } from './shop.js';

/**
 * Всё для первого экрана одним запросом (03 §2 «Bootstrap»). Поля перечислены в спецификации словами;
 * имена и формы — решение каркаса Р-02.
 */
export const Bootstrap = strict({
  player: Player,
  wallet: Wallet,
  tutorialStep: TutorialStep,
  equipped: z.array(Item),
  garage: strict({ zones: z.array(GarageZoneBrief) }),
  coop: CoopBrief.nullable(),
  activeBattle: BattleState.nullable(),
  features: z.record(z.string(), z.boolean()),
  /** Версия и значения клиентского конфига (тексты шансов, цены для показа). */
  clientConfig: strict({ version: z.int().nonnegative(), values: z.record(z.string(), z.unknown()) }),
  content: ContentPointer.nullable(),
  storyChaptersOpen: z.array(z.string()),
  unreadMail: z.int().nonnegative(),
  serverTime: IsoTime,
  /** Возвраты звёзд, ещё не подтверждённые игроком (Р-86); необязательно ради моков клиента. */
  refundNotices: z.array(RefundNotice).optional(),
});
export type Bootstrap = z.infer<typeof Bootstrap>;

export const getBootstrap = endpoint({
  id: 'player.bootstrap', method: 'GET', path: '/bootstrap', auth: true, signed: false, idempotency: 'read', rate: 'read',
  query: strict({ since: z.coerce.number().int().nonnegative().optional() }),
  response: Bootstrap, status: 200, errors: [], spec: 'tech/03 §2, §3, §19', task: 'E02-T01',
});

export const getMe = endpoint({
  id: 'player.me', method: 'GET', path: '/me', auth: true, signed: false, idempotency: 'read', rate: 'read',
  response: Player, status: 200, errors: [], spec: 'tech/03 §3', task: 'E05-T01',
});

/** Распределение свободных очков (ГД 01 §3). Хотя бы одно поле > 0 — иначе 400. */
export const SpendStatsBody = strict({
  str: z.int().nonnegative().optional(),
  agi: z.int().nonnegative().optional(),
  vit: z.int().nonnegative().optional(),
  cha: z.int().nonnegative().optional(),
}).refine((b) => (b.str ?? 0) + (b.agi ?? 0) + (b.vit ?? 0) + (b.cha ?? 0) > 0, 'нужно распределить хотя бы одно очко');

export const spendStats = endpoint({
  id: 'player.stats', method: 'POST', path: '/me/stats', auth: true, signed: true, idempotency: 'key', rate: 'write',
  body: SpendStatsBody, response: Player, status: 200, errors: ['NOT_ENOUGH_POINTS'],
  spec: 'tech/03 §3', task: 'E05-T01',
});

export const getWallet = endpoint({
  id: 'player.wallet', method: 'GET', path: '/me/wallet', auth: true, signed: false, idempotency: 'read', rate: 'read',
  response: Wallet, status: 200, errors: [], spec: 'tech/03 §3', task: 'E01-T03',
});

export const acceptConsent = endpoint({
  id: 'player.consents', method: 'POST', path: '/me/consents', auth: true, signed: true, idempotency: 'natural', rate: 'write',
  body: strict({ docId: DocId, version: z.string().min(1).max(32) }),
  response: strict({ accepted: z.array(DocRef) }), status: 200, errors: ['VERSION_OUTDATED'],
  spec: 'tech/03 §3 (Т-К-59)', task: 'E02-T06',
});

/** Строка истории операций (03 §3 `GET /me/ledger`: «последние операции» — форма не задана, Р-02). */
export const LedgerItem = strict({
  txId: z.string(),
  kind: z.string(),
  currency: CurrencyCode,
  amount: z.int(),
  balanceAfter: z.int(),
  ref: strict({ type: z.string(), id: z.string() }).nullable(),
  at: IsoTime,
});

export const getLedger = endpoint({
  id: 'player.ledger', method: 'GET', path: '/me/ledger', auth: true, signed: false, idempotency: 'read', rate: 'read',
  query: PageQuery.extend({ currency: CurrencyCode.optional() }),
  response: strict({ items: z.array(LedgerItem), nextCursor: NextCursor }), status: 200, errors: [],
  spec: 'tech/03 §3', task: 'E01-T03',
});

/** Шаги обучения T01…T10 (ГД 12 §3). В ответе `step` — текущий шаг ПОСЛЕ действия (Р-02). */
export const tutorialStep = endpoint({
  id: 'tutorial.step', method: 'POST', path: '/tutorial/step', auth: true, signed: true, idempotency: 'natural', rate: 'write',
  body: strict({
    step: z.string().regex(/^T(0[1-9]|10)$/),
    action: z.string().min(1).max(32).optional(),
  }),
  response: strict({ step: TutorialStep, rewards: Grant.optional() }), status: 200, errors: ['STEP_OUT_OF_ORDER'],
  spec: 'tech/03 §3; ГД 12 §3', task: 'E16-T01',
});

export const tutorialSkip = endpoint({
  id: 'tutorial.skip', method: 'POST', path: '/tutorial/skip', auth: true, signed: true, idempotency: 'state', rate: 'write',
  body: Empty, response: strict({ step: z.literal('done') }), status: 200, errors: ['FEATURE_DISABLED'],
  spec: 'tech/03 §3', task: 'E16-T01',
});

/** Сброс характеристик (ГД 01 §3.1, economy/02 §8): первый в сезоне бесплатно, дальше ×2 до ×16. */
export const resetStats = endpoint({
  id: 'player.stats.reset', method: 'POST', path: '/me/stats/reset', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  body: strict({ expectedPriceNuts: z.int().nonnegative() }), response: strict({ player: Player, wallet: Wallet }), status: 200,
  errors: ['PRICE_CHANGED', 'IN_BATTLE', 'NOT_ENOUGH_NUTS', 'REQUIREMENT_NOT_MET'], spec: 'tech/03 §3; economy/02 §8', task: 'E05-T21',
});

/** Термос (канон §10): 10/20/30/40/50 талонов, не больше 5 в сутки, +50 энергии. */
export const buyThermos = endpoint({
  id: 'energy.thermos', method: 'POST', path: '/energy/thermos', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  body: strict({}), response: strict({ wallet: Wallet, thermosLeftToday: z.int().nonnegative(), nextPriceTalons: z.int().positive().nullable() }), status: 200,
  errors: ['DAILY_LIMIT', 'NOT_ENOUGH_TALONS', 'TALONS_DEBT'], spec: 'tech/03 §3; economy/06 §4', task: 'E12-T06',
});

// ---------------- удаление аккаунта и выгрузка данных (tech/08 §8, ADR-0029; E02-T05, Р-90) ----------------

/** Удаление аккаунта: исполняется через 7 дней; вход в игру или `/me/delete/cancel` его отменяет. */
export const deleteAccount = endpoint({
  id: 'account.delete', method: 'POST', path: '/me/delete', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  body: strict({ confirm: z.literal('УДАЛИТЬ') }), response: strict({ scheduledAt: IsoTime }), status: 200,
  errors: ['HAS_PENDING_PAYMENTS', 'DELETE_ALREADY_SCHEDULED'], spec: 'tech/03 §3; tech/08 §8', task: 'E02-T05',
});

export const cancelAccountDelete = endpoint({
  id: 'account.delete.cancel', method: 'POST', path: '/me/delete/cancel', auth: true, signed: true, idempotency: 'state', rate: 'write',
  body: strict({}), response: strict({ status: z.literal('active') }), status: 200, errors: ['NOT_SCHEDULED'], spec: 'tech/03 §3; tech/08 §8', task: 'E02-T05',
});

/** Выгрузка своих данных (152-ФЗ): архив JSON собирает воркер за ≤ 24 ч, не чаще раза в 7 суток. */
export const requestExport = endpoint({
  id: 'account.export', method: 'POST', path: '/me/export', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  body: strict({}), response: strict({ exportId: Uuid7, readyBy: IsoTime }), status: 202, errors: ['DAILY_LIMIT'], spec: 'tech/03 §3; tech/08 §8', task: 'E02-T05',
});

export const ExportStatus = z.enum(['queued', 'ready', 'expired']);

export const getExport = endpoint({
  id: 'account.export.get', method: 'GET', path: '/me/export/{exportId}', auth: true, signed: false, idempotency: 'read', rate: 'read',
  params: strict({ exportId: Uuid7 }),
  response: strict({ status: ExportStatus, url: z.string().url().optional(), expiresAt: IsoTime.optional() }), status: 200,
  errors: ['NOT_FOUND'], spec: 'tech/03 §3; tech/08 §8', task: 'E02-T05',
});

/** Подписанная ссылка на архив (живёт 24 ч): открывается из бота без входа — права даёт подпись. */
export const downloadExport = endpoint({
  id: 'account.export.download', method: 'GET', path: '/exports/{exportId}', auth: false, signed: false, idempotency: 'read', rate: 'none',
  params: strict({ exportId: Uuid7 }), query: strict({ exp: z.coerce.number().int().positive(), sig: z.string().regex(/^[0-9a-f]{64}$/) }),
  response: z.record(z.string(), z.unknown()), status: 200, errors: ['NOT_FOUND', 'GONE', 'SIGNATURE_INVALID'], spec: 'tech/03 §3; tech/08 §8', task: 'E02-T05',
});

// ---------------- профиль и настройки (tech/03 §3; E02-T09, Р-100) ----------------

/** PATCH /me: «Не показывать меня в хронике» и настройки, переносимые между устройствами (частичное обновление). */
export const updateMe = endpoint({
  id: 'player.update', method: 'PATCH', path: '/me', auth: true, signed: true, idempotency: 'state', rate: 'write',
  body: strict({ hideFromChronicle: z.boolean().optional(), settings: PlayerSettings.optional() }),
  response: Player, status: 200, errors: ['FEATURE_DISABLED'], spec: 'tech/03 §3; ux/11 §2.1', task: 'E02-T09',
});

export const NotifyChannels = strict({
  trip_done: z.boolean(), coop_call: z.boolean(), raid_start: z.boolean(), energy_full: z.boolean(), news: z.boolean(),
});
const NotificationsView = strict({ botWriteAccess: z.boolean(), channels: NotifyChannels });

/** Сообщения от бота (tech/03 §3, Т-К-13): доступ на запись в личку и каналы уведомлений. */
export const getNotifications = endpoint({
  id: 'player.notifications', method: 'GET', path: '/me/notifications', auth: true, signed: false, idempotency: 'read', rate: 'read',
  response: NotificationsView, status: 200, errors: [], spec: 'tech/03 §3', task: 'E02-T09',
});

export const putNotifications = endpoint({
  id: 'player.notifications.put', method: 'PUT', path: '/me/notifications', auth: true, signed: true, idempotency: 'state', rate: 'write',
  body: strict({ channels: NotifyChannels.partial(), writeAccessResult: z.enum(['allowed', 'denied']).optional() }),
  response: NotificationsView, status: 200, errors: [], spec: 'tech/03 §3', task: 'E02-T09',
});
